/**
 * progress-flow 어댑터 들이 공유하는 Firestore FlowDocument fetcher.
 *
 * `/api/v1/flows/{flowId}` 엔드포인트를 호출해 Firestore `playground_flows` 컬렉션에
 * 저장된 FlowDocument 를 받는다. `CounselStartFlowAdapter` / `HouseStructureFlowAdapter`
 * 가 사용하던 inline 헬퍼를 단일 모듈로 통합한 것이며, 동작은 동일하다.
 *
 * 사용 예:
 *   const fetchPackingFlow = useMemo(
 *     () => createProgressFlowFetcher('progress-packing'),
 *     [],
 *   );
 *   <InquiryFlowLoader fetcher={fetchPackingFlow} ... />
 *
 * 등록된 flowId 는 `application/scripts/register-flows.ts` 를 참조.
 *
 * 실패 정책: 응답이 ok 가 아니면 throw — `InquiryFlowLoader` 의 onError 가 받아
 * 어댑터의 fallback (toast + onCancel) 로 자연 회귀된다.
 */

import type { FlowDocument } from '@miso/design-system';

const FLOW_API_BASE = '/api/v1/flows';

export async function fetchFlowDocument(flowId: string): Promise<FlowDocument> {
  const res = await fetch(`${FLOW_API_BASE}/${encodeURIComponent(flowId)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { error?: string }).error ?? `Flow '${flowId}' 로드 실패 (${res.status})`
    );
  }
  return res.json() as Promise<FlowDocument>;
}

/**
 * 안정된 fetcher 참조를 만든다 — useMemo([]) 와 결합해 부모 리렌더 시 InquiryFlowLoader
 * 가 매번 새 fetcher identity 로 re-fetch 하는 회귀를 피한다.
 */
export function createProgressFlowFetcher(flowId: string): () => Promise<FlowDocument> {
  return () => fetchFlowDocument(flowId);
}
